import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Eye, RefreshCw, AlertCircle, Loader, Search, Filter, Calendar, User, FileBarChart } from 'lucide-react';
import { supabase, STORAGE_BUCKET } from '../lib/supabase';

interface FileItem {
  id: string;
  name: string;
  path: string;
  size: number;
  type: string;
  created_at: string;
  metadata?: any;
  // Parsed metadata from filename
  submissionId?: string;
  filerName?: string;
  originalFileName?: string;
  // Filing type information
  filingType?: string;
  expressComment?: number;
  // Express comment specific
  isExpressComment?: boolean;
  textData?: string;
}

export const FilingsPage: React.FC = () => {
  const { docketNumber } = useParams<{ docketNumber: string }>();
  const navigate = useNavigate();
  
  // File management state
  const [files, setFiles] = useState<FileItem[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [expressCommentText, setExpressCommentText] = useState<string>('');
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filingTypeFilter, setFilingTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');

  // Load files from Supabase when component mounts
  useEffect(() => {
    if (docketNumber) {
      loadFiles();
    }
  }, [docketNumber]);

  // Filter and search files
  useEffect(() => {
    let filtered = [...files];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(file => 
        (file.originalFileName || file.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (file.filerName || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filing type filter
    if (filingTypeFilter !== 'all') {
      filtered = filtered.filter(file => file.filingType === filingTypeFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.originalFileName || a.name).localeCompare(b.originalFileName || b.name);
        case 'size':
          return b.size - a.size;
        case 'date':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    setFilteredFiles(filtered);
  }, [files, searchTerm, filingTypeFilter, sortBy]);

  const loadFiles = async () => {
    if (!docketNumber) return;

    try {
      setLoading(true);
      setError(null);

      // First, get all subdirectories in the docket folder
      const { data: subdirectories, error: listError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(`ecfs/${docketNumber}`, {
          limit: 1000,
          offset: 0,
        });

      if (listError) {
        throw listError;
      }

      console.log('📁 Docket subdirectories:', subdirectories);

      // Filter for subdirectories only (folders have null id)
      const filingTypeFolders = (subdirectories || []).filter(item => item.id === null);

      // Load files from each filing type subdirectory
      const allFiles: FileItem[] = [];
      for (const folder of filingTypeFolders) {
        const subdirectoryPath = `ecfs/${docketNumber}/${folder.name}`;
        console.log(`📂 Loading files from: ${subdirectoryPath}`);
        await loadFilesRecursively(subdirectoryPath, allFiles);
      }

      // Also check for files directly in the docket folder (not in subdirectories)
      const directFiles = (subdirectories || []).filter(item => item.id !== null);
      for (const file of directFiles) {
        const filePath = `ecfs/${docketNumber}/${file.name}`;
        console.log(`📄 Processing direct file: ${filePath}`);
        const fileItem = await createFileItem(file, filePath);
        allFiles.push(fileItem);
      }

      setFiles(allFiles);
      console.log('Files loaded:', allFiles.length, 'files total');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const loadFilesRecursively = async (folderPath: string, allFiles: FileItem[]): Promise<void> => {
    try {
      const { data: storageItems, error: listError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(folderPath, {
          limit: 1000,
          offset: 0,
        });

      if (listError) {
        console.warn(`Failed to list ${folderPath}:`, listError);
        return;
      }

      if (!storageItems) return;

      for (const item of storageItems) {
        const itemPath = `${folderPath}/${item.name}`;
        
        if (item.id === null) {
          // This is a folder, recursively search it
          await loadFilesRecursively(itemPath, allFiles);
        } else {
          // This is a file, add it to our collection
          const fileItem = await createFileItem(item, itemPath);
          allFiles.push(fileItem);
        }
      }
    } catch (err) {
      console.warn(`Error loading files from ${folderPath}:`, err);
    }
  };

  const getFileMetadata = async (filePath: string) => {
    try {
      // Try to get file info with metadata
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(filePath.split('/').slice(0, -1).join('/'), {
          limit: 1000,
          offset: 0,
        });
      
      if (error) {
        console.warn('Failed to get metadata for', filePath, error);
        return null;
      }
      
      const fileName = filePath.split('/').pop();
      const fileInfo = data?.find(item => item.name === fileName);
      return fileInfo?.metadata || null;
    } catch (err) {
      console.warn('Error getting metadata for', filePath, err);
      return null;
    }
  };

  const createFileItem = async (file: any, filePath: string): Promise<FileItem> => {
    // Parse filename to extract metadata
    const parts = file.name.split('___');
    let submissionId: string | undefined;
    let filerName: string | undefined;
    let originalFileName: string | undefined;

    if (parts.length >= 3) {
      submissionId = parts[0] !== 'UNKNOWN' ? parts[0] : undefined;
      filerName = parts[1];
      originalFileName = parts.slice(2).join('___');
    } else {
      originalFileName = file.name;
    }

    // Extract filing type from folder path
    const pathParts = filePath.split('/');
    const filingType = pathParts.length > 2 ? pathParts[pathParts.length - 2] : undefined;
    
    // Try to get metadata if not available
    let metadata = file.metadata;
    if (!metadata) {
      metadata = await getFileMetadata(filePath);
    }
    
    // Debug logging for Express comment detection
    console.log('🔍 File analysis:', {
      fileName: file.name,
      filePath,
      pathParts,
      filingType,
      metadata,
      hasMetadata: !!metadata,
      metadataKeys: metadata ? Object.keys(metadata) : []
    });
    
    // Check if this is an express comment using multiple detection methods
    const isExpressComment = 
      // Path-based detection
      pathParts.includes('EXPRESS-COMMENT') ||
      // Metadata-based detection
      metadata?.contentType === 'express_comment' ||
      metadata?.expressComment === 1 ||
      // Filename-based detection (fallback)
      file.name.toLowerCase().includes('express') ||
      // Content type detection
      file.metadata?.mimetype === 'text/plain' && 
      (metadata?.textDataLength > 0 || file.size < 10000); // Small text files might be express comments
    
    console.log('⚡ Express comment check:', {
      fileName: file.name,
      pathIncludesExpress: pathParts.includes('EXPRESS-COMMENT'),
      contentTypeExpress: metadata?.contentType === 'express_comment',
      expressCommentFlag: metadata?.expressComment === 1,
      isExpressComment
    });

    return {
      id: file.id || file.name,
      name: file.name,
      path: filePath,
      size: file.metadata?.size || 0,
      type: file.metadata?.mimetype || (isExpressComment ? 'text/plain' : 'application/octet-stream'),
      created_at: file.created_at || file.updated_at || new Date().toISOString(),
      metadata: file.metadata,
      submissionId,
      filerName,
      originalFileName,
      filingType,
      expressComment: file.metadata?.expressComment || undefined,
      isExpressComment,
    };
  };

  const loadExpressCommentText = async (file: FileItem) => {
    if (!file.isExpressComment) return;
    
    try {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .download(file.path);

      if (error) {
        throw error;
      }

      const text = await data.text();
      setExpressCommentText(text);
    } catch (err) {
      console.error('Failed to load express comment text:', err);
      setExpressCommentText('Failed to load comment text');
    }
  };

  const handleFileDownload = async (file: FileItem) => {
    try {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .download(file.path);

      if (error) {
        throw error;
      }

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.originalFileName || file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get count of files per filer
  const getFilerFileCount = (filerName: string) => {
    const count = files.filter(file => file.filerName === filerName).length;
    console.log(`🔢 FilingsPage: Filer "${filerName}" has ${count} files`);
    return count;
  };

  // Get unique filing types for filter
  const filingTypes = Array.from(new Set(files.map(file => file.filingType).filter(Boolean)));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header with Breadcrumb */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/library')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to My Dockets</span>
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Documents - Docket {docketNumber}
            </h1>
            <p className="text-gray-600 mt-2">
              {files.length} documents • {filteredFiles.length} shown
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={loadFiles}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span>Refresh</span>
            </button>
            
            {/* Temporary test button for Express tags */}
            <button
              onClick={() => {
                // Add a test Express comment file to verify UI
                const testFile: FileItem = {
                  id: 'test-express',
                  name: 'TEST_EXPRESS_COMMENT.txt',
                  path: 'ecfs/test/EXPRESS-COMMENT/test.txt',
                  size: 1000,
                  type: 'text/plain',
                  created_at: new Date().toISOString(),
                  metadata: { contentType: 'express_comment', expressComment: 1 },
                  submissionId: 'TEST123',
                  filerName: 'Test Filer',
                  originalFileName: 'Test Express Comment',
                  filingType: 'EXPRESS-COMMENT',
                  expressComment: 1,
                  isExpressComment: true,
                };
                setFiles(prev => [testFile, ...prev]);
                console.log('🧪 Added test Express comment file');
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors duration-200"
            >
              <span>🧪 Test Express Tag</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search documents or filers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Filing Type Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={filingTypeFilter}
              onChange={(e) => setFilingTypeFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
            >
              <option value="all">All Filing Types</option>
              {filingTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          {/* Sort */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'size')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="size">Sort by Size</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading documents...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 text-center">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md mx-auto">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
              <h3 className="text-red-800 font-semibold mb-2">Error Loading Documents</h3>
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={loadFiles}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredFiles.length === 0 && (
          <div className="p-8 text-center">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 max-w-md mx-auto">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-gray-900 font-semibold mb-2">
                {searchTerm || filingTypeFilter !== 'all' ? 'No Documents Match Your Filters' : 'No Documents Found'}
              </h3>
              <p className="text-gray-600 text-sm">
                {searchTerm || filingTypeFilter !== 'all' 
                  ? 'Try adjusting your search terms or filters.'
                  : 'No documents have been processed for this docket yet.'
                }
              </p>
            </div>
          </div>
        )}

        {/* Documents List */}
        {!loading && !error && filteredFiles.length > 0 && (
          <div className="divide-y divide-gray-200">
            {filteredFiles.map((file) => (
              <div
                key={file.id}
                className="p-6 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        file.isExpressComment 
                          ? 'bg-orange-100' 
                          : 'bg-blue-100'
                      }`}>
                        {file.isExpressComment ? (
                          <span className="text-orange-600 text-lg">⚡</span>
                        ) : (
                          <FileText className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate" title={file.originalFileName || file.name}>
                        {file.originalFileName || file.name}
                      </h3>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">{file.filerName || 'Unknown Filer'}</span>
                            {/* Document Count Tag - only show if more than 1 file */}
                            {file.filerName && (() => {
                              const fileCount = getFilerFileCount(file.filerName);
                              console.log(`🏷️ FilingsPage: Rendering count tag for "${file.filerName}": ${fileCount} files`);
                              return fileCount > 1 ? (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full border border-blue-200 flex-shrink-0">
                                  {fileCount} docs
                                </span>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <FileBarChart className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">{formatFileSize(file.size)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">{formatDate(file.created_at)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {file.filingType && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                              {file.filingType}
                            </span>
                          )}
                          {file.isExpressComment && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                              Express
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {file.submissionId && (
                        <p className="text-gray-500 text-xs mt-2">
                          Submission ID: {file.submissionId}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => {
                        setSelectedFile(file);
                        if (file.isExpressComment) {
                          loadExpressCommentText(file);
                        }
                      }}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors duration-200 text-sm ${
                        file.isExpressComment
                          ? 'bg-orange-50 hover:bg-orange-100 text-orange-700 hover:text-orange-800'
                          : 'bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800'
                      }`}
                    >
                      <Eye className="w-4 h-4" />
                      <span>{file.isExpressComment ? 'View Text' : 'Preview'}</span>
                    </button>
                    
                    <button
                      onClick={() => handleFileDownload(file)}
                      className="flex items-center space-x-1 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 hover:text-green-800 rounded-lg transition-colors duration-200 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* File Preview Modal */}
      {selectedFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative max-w-4xl w-full max-h-screen bg-white border border-gray-300 rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-300">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 truncate" title={selectedFile.originalFileName || selectedFile.name}>
                  {selectedFile.originalFileName || selectedFile.name}
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  {formatFileSize(selectedFile.size)} • {formatDate(selectedFile.created_at)}
                  {selectedFile.filerName && ` • ${selectedFile.filerName}`}
                </p>
              </div>
              
              <button
                onClick={() => setSelectedFile(null)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {selectedFile.isExpressComment ? (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <span className="text-orange-600 text-2xl">⚡</span>
                    <h3 className="text-lg font-semibold text-gray-900">Express Comment</h3>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                      {expressCommentText || 'Loading comment text...'}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => handleFileDownload(selectedFile)}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors duration-200"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download as Text</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    File preview not available
                  </p>
                  <p className="text-gray-500 text-sm mb-6">
                    {selectedFile.type} • {selectedFile.originalFileName || selectedFile.name}
                  </p>
                  <button
                    onClick={() => handleFileDownload(selectedFile)}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download to view</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};